import React, { useState } from 'react'
import { Dialog, DialogTitle, DialogContent, makeStyles, IconButton } from '@material-ui/core'
import firebase from 'firebase/app'
import { toast } from 'utils/notify'
import ProgressButton from 'components/ProgressButton'
import { useFormik } from 'formik'
import { Close as CloseIcon } from '@material-ui/icons'
import { signOut } from 'store/auth'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import PasswordField from './PasswordField'
import { changePasswordSchema } from 'schemas/admins'

const ChangePasswordModal = ({ open, onClose }) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const classes = useStyles()
  const [inProgress, setInProgress] = useState(false)

  const handleLogout = async () => {
    await dispatch(signOut())
    localStorage.removeItem('badgeOrientation')
    history.push('/')
  }

  const formik = useFormik({
    initialValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    },
    validationSchema: changePasswordSchema,
    onSubmit: async (values) => {
      try {
        if (values.currentPassword === values.newPassword) {
          toast.warning('New password must differ from the current one.')
          return
        }
        setInProgress(true)
        const user = firebase.auth().currentUser
        // Get credentials with current password for reauthentication
        const credential = firebase.auth.EmailAuthProvider.credential(
          user.email,
          values.currentPassword
        )
        // Reauthenticate user
        await user.reauthenticateWithCredential(credential)
        // Update password and logout
        await user.updatePassword(values.newPassword)
        await handleLogout()
        toast.success('Password updated. Please log in again.')
        onClose()
        formik.resetForm()
      } catch (error) {
        if (error.code === 'auth/wrong-password') {
          toast.error('Incorrect current password.')
        } else if (error.code === 'auth/requires-recent-login') {
          toast.error('Session expired. Please log in again to change your password.')
        } else {
          toast.error('Password update failed: ' + error.message)
        }
      } finally {
        setInProgress(false)
      }
    }
  })

  const handleClose = () => {
    formik.resetForm()
    onClose()
  }

  return (
    <Dialog open={open} maxWidth="sm" fullWidth>
      <DialogTitle>
        Change Password
        <IconButton
          aria-label="close"
          style={{
            padding: '0.5rem',
            position: 'absolute',
            right: 8,
            top: 8
          }}
          onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <form className={classes.form} onSubmit={formik.handleSubmit}>
          <PasswordField label="Current Password" name="currentPassword" formik={formik} />
          <PasswordField label="New Password" name="newPassword" formik={formik} />
          <PasswordField label="Confirm New Password" name="confirmPassword" formik={formik} />
        </form>

        <ProgressButton
          onClick={formik.handleSubmit}
          disabled={!formik.isValid || !formik.dirty || inProgress}
          label="Update Password"
          inProgress={inProgress}
          className={classes.actionButton}
        />
      </DialogContent>
    </Dialog>
  )
}

export default ChangePasswordModal

const useStyles = makeStyles({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem',
    padding: '1rem 0'
  },
  actions: {
    padding: '1rem'
  },
  actionButton: {
    margin: '1.25rem 0px',
    width: '100%'
  }
})
